/** @jsx jsx */
import { useEffect, useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import ExternalResourceIcon from '@helpCenter/topics/src/common/ui/external-resource-fallback-icon';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { Link } from 'view/common/analytics';
import messages from 'view/help-center-topics/topic/messages';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import { AnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { AutoScaledImage } from '@atlassian/help-center-common-component/auto-scaled-image';
import { ThemedLink } from '@atlassian/help-center-common-component/themed-link';
import { isExternalResource, urlRefsAndTarget } from '@atlassian/help-center-common-util/is-external-resource';
import { getUpdatedDisplayLink } from '@atlassian/help-center-common-util/url';
import type { topicCardFragment$key } from './__generated__/topicCardFragment.graphql';
import type { iconTypes } from 'topics/src/common/ui/external-resource-fallback-icon/types';

const topicNameLinkStyles = css({
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: token('font.weight.medium', '500'),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    '@media (min-width: 480px)': {
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: token('font.weight.semibold', '600'),
    },
});

const topicNameWithoutLinkStyles = css({
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: token('font.weight.medium', '500'),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    color: token('color.text', '#172B4D'),
    '@media (min-width: 480px)': {
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: token('font.weight.semibold', '600'),
    },
});

const helpObjectsStyles = css({
    fontSize: '12px',
    lineHeight: '16px',
    listStyleType: 'none',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: token('space.200', '16px'),
    padding: token('space.0', '0rem'),
    '@media (min-width: 480px)': {
        // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
        fontSize: token('font.size.200', '14px'),
        // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
        lineHeight: token('font.lineHeight.200', '20px'),
    },
});

const listItemStyles = css({
    marginTop: 0,
});

const containerStyles = css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    height: '100%',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '& ol': {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
        margin: `${token('space.200', '16px')} 0 0 !important`,
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '& a': {
        marginTop: 0,
    },
});

const styledHelpObjectStyles = css({
    lineHeight: token('space.300', '24px'),
    display: 'flex',
    marginBottom: token('space.150', '0.75rem'),
});

const imageContainerStyles = css({
    height: '24px',
    width: '24px',
    marginRight: token('space.100', '8px'),
    flexShrink: 0,
});

const helpObjectTitleStyles = css({
    whiteSpace: 'nowrap',
    width: '100%',
    color: token('color.text', '#172B4D'),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
});

const showMoreLinkStyles = css({
    marginTop: 'auto',
});

const topicLinkStyles = css({
    overflow: 'hidden',
});

const textContentStyles = css({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const showMoreWithoutLinkStyles = css({
    color: token('color.text', '#172B4D'),
});

interface Props {
    topic: topicCardFragment$key;
}

export const Topic = (props: Props & WithAnalyticsEventsProps) => {
    di(useIsPageInEditMode);
    const { topicName, items, topicId } = useFragment<topicCardFragment$key>(
        graphql`
            fragment topicCardFragment on HelpLayoutTopic {
                topicName
                topicId
                items {
                    __id
                    title
                    logo
                    displayLink
                    entityKey
                    helpObjectType
                }
            }
        `,
        props.topic
    );

    const intl = useIntl();
    const [isPageInEditMode] = useIsPageInEditMode();
    const MAX_HELP_OBJECTS = 3;

    const allHelpObjects = useMemo(() => items || [], [items]);

    useEffect(() => {
        !isPageInEditMode &&
            allHelpObjects.forEach((o) => {
                if (!o?.title) {
                    const { createAnalyticsEvent } = props;

                    if (createAnalyticsEvent) {
                        const analyticsEvent = createAnalyticsEvent({
                            analyticsType: OPERATIONAL_EVENT_TYPE,
                            action: 'invalidHelpObjectFound',
                            actionSubject: 'helpObject',
                        });
                        analyticsEvent.fire();
                    }
                }
            });
    }, [isPageInEditMode, allHelpObjects, props]);

    const showMoreCount = allHelpObjects.length - MAX_HELP_OBJECTS;

    const getResourceType = (helpObjectType: string): iconTypes => {
        switch (helpObjectType) {
            case 'HelpObjectStoreArticle':
                return 'ARTICLE';
            case 'HelpObjectStoreRequestForm':
                return 'REQUEST_FORM';
            case 'HelpObjectStoreChannel':
                return 'CHANNEL';
            default:
                return 'REQUEST_FORM';
        }
    };

    if (allHelpObjects.length) {
        return (
            <div css={containerStyles}>
                {isPageInEditMode ? (
                    <div css={topicNameWithoutLinkStyles}>
                        <div css={textContentStyles}>{topicName}</div>
                    </div>
                ) : (
                    <ThemedLink
                        css={topicNameLinkStyles}
                        to={topicId ? `/topic/${topicId}` : ''}
                        actionSubjectId={'topicTitle'}
                    >
                        <Tooltip content={topicName}>
                            <div css={textContentStyles}>{topicName}</div>
                        </Tooltip>
                    </ThemedLink>
                )}
                <ol css={helpObjectsStyles}>
                    {allHelpObjects.slice(0, MAX_HELP_OBJECTS).map((helpObject, index) => (
                        <li key={helpObject?.__id} css={listItemStyles}>
                            <AnalyticsContext data={{ attributes: { helpObjectPosition: index + 1 } }}>
                                {helpObject && (
                                    <div css={styledHelpObjectStyles}>
                                        <div css={imageContainerStyles}>
                                            {helpObject.logo && (
                                                <AutoScaledImage
                                                    src={helpObject.logo}
                                                    alt={helpObject.title ?? ''}
                                                    inheritWidth
                                                />
                                            )}
                                            {!helpObject.logo && helpObject?.helpObjectType ? (
                                                <ExternalResourceIcon
                                                    iconUrl={getUpdatedDisplayLink(helpObject?.displayLink || '')}
                                                    resourceType={getResourceType(helpObject?.helpObjectType)}
                                                    size={'medium'}
                                                />
                                            ) : null}
                                        </div>
                                        {isPageInEditMode ? (
                                            <div css={helpObjectTitleStyles}>{helpObject.title}</div>
                                        ) : (
                                            <Link
                                                css={topicLinkStyles}
                                                {...urlRefsAndTarget({
                                                    isAnExternalResource: isExternalResource(
                                                        helpObject?.entityKey as string
                                                    ),
                                                    displayLink: getUpdatedDisplayLink(helpObject?.displayLink || ''),
                                                })}
                                                actionSubjectId="helpObject"
                                            >
                                                <Tooltip content={helpObject.title}>
                                                    <div css={helpObjectTitleStyles}>{helpObject.title}</div>
                                                </Tooltip>
                                            </Link>
                                        )}
                                    </div>
                                )}
                            </AnalyticsContext>
                        </li>
                    ))}
                </ol>
                {showMoreCount > 0 &&
                    (isPageInEditMode ? (
                        <div css={showMoreWithoutLinkStyles}>
                            <div css={textContentStyles}>
                                {intl.formatMessage(messages.showMoreText, {
                                    count: showMoreCount,
                                })}
                            </div>
                        </div>
                    ) : (
                        <ThemedLink
                            css={showMoreLinkStyles}
                            to={topicId ? `/topic/${topicId}` : ''}
                            actionSubjectId={'showMoreHelpObjects'}
                        >
                            <div css={textContentStyles}>
                                {intl.formatMessage(messages.showMoreText, {
                                    count: showMoreCount,
                                })}
                            </div>
                        </ThemedLink>
                    ))}
            </div>
        );
    }
    return null;
};

export default withAnalyticsEvents()(Topic);
